import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  CreditCard,
  Calendar,
  Receipt,
  ExternalLink,
  AlertTriangle,
  ArrowUpRight,
  RefreshCw,
  Loader2,
  Crown,
  Zap,
  Shield,
  XCircle,
} from 'lucide-react';
import { subscriptionApi } from './api';

const STATUS_CONFIG = {
  active: { label: 'Active', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  trialing: { label: 'Trial', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  past_due: { label: 'Past Due', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  canceled: { label: 'Canceled', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  unpaid: { label: 'Unpaid', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  incomplete: { label: 'Incomplete', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  free: { label: 'Free', className: 'bg-gray-500/10 text-gray-600 border-gray-500/20' },
};

const PLAN_ICONS = { free: Shield, pro: Zap, master: Crown };
const PLAN_LABELS = { free: 'Free', pro: 'Pro', master: 'Master' };

function formatDate(dateStr) {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function formatCurrency(amount, currency = 'EUR') {
  if (amount == null) return '--';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(amount / 100);
}

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [billing, setBilling] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [targetPlan, setTargetPlan] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subData, billingData] = await Promise.all([
        subscriptionApi.getCurrentSubscription(),
        subscriptionApi.getBillingHistory().catch(() => []),
      ]);
      setSub(subData);
      setBilling(Array.isArray(billingData) ? billingData : billingData?.invoices || []);
    } catch (err) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await subscriptionApi.cancelSubscription(cancelImmediate);
      toast.success(cancelImmediate
        ? 'Subscription cancelled immediately'
        : 'Subscription will cancel at end of billing period');
      setCancelDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await subscriptionApi.reactivate();
      toast.success('Subscription reactivated!');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!targetPlan) return;
    setActionLoading(true);
    try {
      await subscriptionApi.changePlan(targetPlan);
      toast.success(`Plan changed to ${PLAN_LABELS[targetPlan] || targetPlan}`);
      setChangePlanDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to change plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    try {
      const data = await subscriptionApi.getPortalUrl();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error('Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const plan = sub?.plan || 'free';
  const status = sub?.status || 'free';
  const statusConf = STATUS_CONFIG[status] || STATUS_CONFIG.free;
  const PlanIcon = PLAN_ICONS[plan] || Shield;
  const isFree = plan === 'free';
  const cancelAtPeriodEnd = sub?.cancel_at_period_end;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">My Subscription</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your plan and billing
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-2 self-start">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Current Plan Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <PlanIcon className="h-5 w-5 text-primary" />
                {PLAN_LABELS[plan] || plan} Plan
              </CardTitle>
              <Badge className={statusConf.className}>
                {statusConf.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {cancelAtPeriodEnd && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-sm">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <span className="text-yellow-700 dark:text-yellow-400">
                  Your subscription will cancel at the end of the current billing period
                  {sub?.current_period_end && ` (${formatDate(sub.current_period_end)})`}.
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {!isFree && sub?.current_period_start && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <div className="text-xs text-muted-foreground">Current Period</div>
                    <div className="text-sm font-medium">
                      {formatDate(sub.current_period_start)} - {formatDate(sub.current_period_end)}
                    </div>
                  </div>
                </div>
              )}

              {!isFree && sub?.next_billing_date && !cancelAtPeriodEnd && (
                <div className="flex items-start gap-3">
                  <Receipt className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <div className="text-xs text-muted-foreground">Next Billing</div>
                    <div className="text-sm font-medium">{formatDate(sub.next_billing_date)}</div>
                  </div>
                </div>
              )}

              {!isFree && sub?.payment_method && (
                <div className="flex items-start gap-3">
                  <CreditCard className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <div className="text-xs text-muted-foreground">Payment Method</div>
                    <div className="text-sm font-medium">
                      {sub.payment_method.type === 'card' ? (
                        <>**** {sub.payment_method.last4}</>
                      ) : sub.payment_method.type === 'paypal' ? (
                        <>{sub.payment_method.email}</>
                      ) : (
                        sub.payment_method.type
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => navigate('/subscription/pricing')}
              >
                <ArrowUpRight className="h-4 w-4" />
                {isFree ? 'Upgrade' : 'Change Plan'}
              </Button>

              {!isFree && !cancelAtPeriodEnd && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-destructive hover:text-destructive"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <XCircle className="h-4 w-4" />
                  Cancel Subscription
                </Button>
              )}

              {cancelAtPeriodEnd && (
                <Button
                  variant="default"
                  size="sm"
                  className="gap-2"
                  disabled={actionLoading}
                  onClick={handleReactivate}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Reactivate
                </Button>
              )}

              {!isFree && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  disabled={actionLoading}
                  onClick={handlePortal}
                >
                  <ExternalLink className="h-4 w-4" />
                  Billing Portal
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Billing History */}
        {billing.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Billing History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Amount</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Provider</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.map((item, i) => (
                      <tr key={item.id || i} className="border-b border-border/50">
                        <td className="p-3">{formatDate(item.date || item.created)}</td>
                        <td className="p-3 font-medium">
                          {formatCurrency(item.amount || item.amount_paid, item.currency)}
                        </td>
                        <td className="p-3">
                          <Badge
                            variant="secondary"
                            className={
                              item.status === 'paid'
                                ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                : item.status === 'failed'
                                  ? 'bg-red-500/10 text-red-600 border-red-500/20'
                                  : ''
                            }
                          >
                            {item.status || 'unknown'}
                          </Badge>
                        </td>
                        <td className="p-3 capitalize">{item.provider || '--'}</td>
                        <td className="p-3 text-right">
                          {item.invoice_url ? (
                            <a
                              href={item.invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              View
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            '--'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Cancel Subscription
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your {PLAN_LABELS[plan]} plan? You will lose access to premium features.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="rounded-lg border p-3 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cancel-type"
                    className="mt-1"
                    checked={!cancelImmediate}
                    onChange={() => setCancelImmediate(false)}
                  />
                  <div>
                    <div className="font-medium text-sm">Cancel at period end</div>
                    <div className="text-xs text-muted-foreground">
                      Keep access until {formatDate(sub?.current_period_end)}. No further charges.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cancel-type"
                    className="mt-1"
                    checked={cancelImmediate}
                    onChange={() => setCancelImmediate(true)}
                  />
                  <div>
                    <div className="font-medium text-sm">Cancel immediately</div>
                    <div className="text-xs text-muted-foreground">
                      Lose access right away. You may receive a prorated refund.
                    </div>
                  </div>
                </label>
              </div>

              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                <strong>Warning:</strong> Cancelling will revoke access to AI features, advanced maps, extended storage, and other premium features included in your plan.
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setCancelDialogOpen(false)} disabled={actionLoading}>
                Keep Subscription
              </Button>
              <Button variant="destructive" onClick={handleCancel} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Cancellation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Plan Confirmation Dialog */}
        <Dialog open={changePlanDialogOpen} onOpenChange={setChangePlanDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Plan</DialogTitle>
              <DialogDescription>
                You are switching from <strong>{PLAN_LABELS[plan]}</strong> to{' '}
                <strong>{PLAN_LABELS[targetPlan] || targetPlan}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="rounded-lg border p-3 text-sm text-muted-foreground">
                Your plan will be updated immediately. Any price difference will be prorated for the remainder of your current billing period.
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setChangePlanDialogOpen(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button onClick={handleChangePlan} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Change'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
